/**
 * 403 Forbidden 페이지
 *
 * **용도:**
 * - 사용자가 접근 권한이 없는 페이지에 접근했을 때 표시되는 공용 403 페이지
 * - Route Guard(ProtectedRoute)에서 권한 체크 실패 시 자동으로 이 페이지를 렌더링
 *
 * **언제 이 페이지가 보이나요?**
 * - URL을 직접 입력해서 권한 없는 페이지에 접근한 경우
 *   - 예: 일반 사용자가 /hr/employees를 브라우저 주소창에 입력
 * - Sidebar 메뉴를 우회해서 접근하려 할 때
 *
 * **권한 체크 흐름:**
 * 1. 사용자가 보호된 라우트(/hr/employees)에 접근 시도
 * 2. ProtectedRoute가 canAccessRoute()로 권한 확인
 * 3. 권한이 없으면 → 이 ForbiddenPage를 표시
 * 4. 권한이 있으면 → 원래 페이지를 표시
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Home, ArrowLeft } from 'lucide-react';

/**
 * ForbiddenPage 컴포넌트
 */
export function ForbiddenPage() {
  const navigate = useNavigate();

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
      padding: '20px',
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        padding: '48px 32px',
      }}>
        {/* 에러 아이콘 */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '24px',
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'var(--color-error-light, #FEE2E2)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <AlertCircle size={48} style={{ color: 'var(--color-error)' }} />
          </div>
        </div>

        {/* 타이틀 */}
        <h1 style={{
          fontSize: '28px',
          fontWeight: 'bold',
          color: 'var(--color-gray-800)',
          marginBottom: '16px',
        }}>
          접근 권한이 없습니다
        </h1>

        {/* 설명 */}
        <p style={{
          fontSize: '16px',
          color: 'var(--color-gray-600)',
          lineHeight: '1.6',
          marginBottom: '32px',
        }}>
          이 페이지는 <strong>인사관리 권한</strong>이 있는 사용자만 접근할 수 있습니다.
          <br />
          권한이 필요하신 경우 시스템 관리자에게 문의해 주세요.
        </p>

        {/* 버튼 그룹 */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}>
          {/* 홈으로 이동 버튼 */}
          <button
            onClick={() => navigate('/')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--color-primary-dark, #0E7490)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--color-primary)';
            }}
          >
            <Home size={18} />
            <span>홈으로 이동</span>
          </button>

          {/* 이전 페이지로 돌아가기 버튼 */}
          <button
            onClick={() => navigate(-1)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: 'white',
              color: 'var(--color-gray-700)',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--color-gray-50)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
            }}
          >
            <ArrowLeft size={18} />
            <span>이전 페이지로</span>
          </button>
        </div>

        {/* 추가 안내 */}
        <div style={{
          marginTop: '32px',
          padding: '16px',
          background: 'var(--color-gray-50)',
          borderRadius: '8px',
          fontSize: '13px',
          color: 'var(--color-gray-600)',
          lineHeight: '1.5',
        }}>
          <strong>💡 참고:</strong> 사이드바 메뉴에서 권한이 있는 페이지만 표시됩니다.
          <br />
          권한 없는 페이지에 URL로 직접 접근하면 이 화면이 나타납니다.
        </div>
      </div>
    </div>
  );
}

/**
 * ============================================
 * 사용 예시 (ProtectedRoute에서)
 * ============================================
 *
 * ```tsx
 * <Route
 *   path="/hr/employees"
 *   element={
 *     <ProtectedRoute path="/hr/employees">
 *       <HrEmployeesPage />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 *
 * 위 코드에서 권한이 없으면 HrEmployeesPage 대신
 * ForbiddenPage가 자동으로 렌더링됩니다.
 */

/**
 * ============================================
 * PM 참고사항
 * ============================================
 *
 * **접근 제어 이중 방어:**
 * 1. Sidebar: 권한 없는 메뉴는 아예 보이지 않음 (UX 개선)
 * 2. ForbiddenPage: URL 직접 입력 시에도 차단 (보안 강화)
 *
 * **권한 부여 대상:**
 * - HR 역할 (hr)
 * - Super Admin 역할 (super_admin)
 *
 * **권한 없는 사용자:**
 * - 일반 직원 (general)
 * - TL (tl)
 * - 조직장 (org_manager) - 인사관리 권한은 없음
 * → 이들은 이 페이지를 보게 됩니다.
 */
